// beui.dev/components/agents/streaming-response

import {
  Check,
  ChevronDown,
  Copy,
  RotateCcw,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import {
  type CitationItem,
  CitationList,
  CitationStack,
} from "@/components/agents/citations";
// Statt `AgentDisclosure`: Dessen Höhe springt zwischen `auto` und 0 — nur
// Deckkraft und Beschnitt laufen weich, das Zuklappen ruckt. Die gemessene
// Höhe des Bouncy-Panels federt in beide Richtungen.
import { BouncyDisclosurePanel } from "@/components/motion/bouncy-accordion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { EASE_OUT, SPRING_PRESS, SPRING_SWAP } from "@/lib/ease";
import { cn } from "@/lib/utils";

export type StreamingResponseStatus = "streaming" | "complete" | "error";
export type StreamingResponseFeedback = "up" | "down" | null;

/** Beschriftungen der Aktionsleiste. Die Oberfläche des Portals spricht Deutsch,
 *  die Vorlage englisch — deshalb sind sie hier von außen setzbar. */
export interface StreamingResponseLabels {
    copy: string;
    copied: string;
    retry: string;
    helpful: string;
    notHelpful: string;
    /** Einzahl/Mehrzahl der Quellenzeile, z. B. „Quelle" / „Quellen". */
    source: string;
    sources: string;
    /** Sprechblase der Quellenzeile, je nach Zustand. */
    showSources: string;
    hideSources: string;
}

const DEFAULT_LABELS: StreamingResponseLabels = {
    copy: "Copy response",
    copied: "Copied",
    retry: "Retry response",
    helpful: "Helpful",
    notHelpful: "Not helpful",
    source: "source",
    sources: "sources",
    showSources: "Show sources",
    hideSources: "Hide sources",
};

export interface StreamingResponseProps {
  /** Rendered response content. Pass plain text or the output of a Markdown renderer. */
  children: ReactNode;
  status?: StreamingResponseStatus;
  /** Plain-text value copied by the built-in copy action. */
  copyText?: string;
  /** Overrides the built-in clipboard action. */
  onCopy?: () => void | Promise<void>;
  onRetry?: () => void;
  /** Optional sources shown as a compact footer disclosure after streaming. */
  sources?: CitationItem[];
  sourcesOpen?: boolean;
  defaultSourcesOpen?: boolean;
  onSourcesOpenChange?: (open: boolean) => void;
  sourceIdPrefix?: string;
  feedback?: StreamingResponseFeedback;
  defaultFeedback?: StreamingResponseFeedback;
  onFeedbackChange?: (feedback: StreamingResponseFeedback) => void;
  /** Set false when a surrounding conversation log announces streamed text. */
  announce?: boolean;
  /** Hides the built-in completion actions without changing response status. */
  showActions?: boolean;
  /**
   * Hides the two thumbs without touching the other actions.
   *
   * Needed where an answer should be copyable but a rating would go nowhere —
   * in the Fragen panel there is no way to attach it to the conversation (see
   * `ChatMessage`, property `compactActions`).
   */
  showFeedback?: boolean;
  labels?: Partial<StreamingResponseLabels>;
  /** `data-testid`s der Aktionsleiste — die Oberflächentests greifen darauf zu. */
  testIds?: Partial<Record<
    "actions" | "copy" | "retry" | "helpful" | "notHelpful" | "sources",
    string
  >>;
  /** Eigene Knöpfe hinter den eingebauten Aktionen, z. B. „als HTML kopieren". */
  extraActions?: ReactNode;
  /** Sperrt die Aktionsleiste, solange irgendwo eine Antwort läuft. */
  actionsDisabled?: boolean;
  className?: string;
  contentClassName?: string;
  actionsClassName?: string;
  /** Klasse der aufgeklappten Quellenliste. */
  sourcesClassName?: string;
}

function ResponseAction({
  label,
  testId,
  active = false,
  // `pressed` statt eines Vergleichs mit dem Label: übersetzte Beschriftungen
  // treffen den englischen Wortlaut nicht mehr, den die Vorlage dafür ansah.
  pressed,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  testId?: string;
  active?: boolean;
  pressed?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  const reduce = useReducedMotion() ?? false;

  const button = (
    <motion.button
      type="button"
      aria-label={label}
      data-testid={testId}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      whileTap={reduce || disabled ? undefined : { scale: 0.9 }}
      transition={SPRING_PRESS}
      className={cn(
        "grid size-7 place-items-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring",
        active && "bg-muted text-foreground",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      {children}
    </motion.button>
  );

  // Beschriftung als Sprechblase statt als `title` des Browsers: Die erscheint
  // sofort, in der Schrift der Oberfläche, und verschwindet nicht nach ein paar
  // Sekunden von selbst.
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        {/* Schriftgrad wie im Tooltip der eingeklappten Sidebar. */}
        <TooltipContent className="text-sm font-medium">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function StreamingResponse({
  children,
  status = "streaming",
  copyText,
  onCopy,
  onRetry,
  sources = [],
  sourcesOpen,
  defaultSourcesOpen = false,
  onSourcesOpenChange,
  sourceIdPrefix,
  feedback,
  defaultFeedback = null,
  onFeedbackChange,
  announce = true,
  showActions = true,
  showFeedback = true,
  labels,
  testIds,
  extraActions,
  actionsDisabled = false,
  className,
  contentClassName,
  actionsClassName,
  sourcesClassName,
}: StreamingResponseProps) {
  const reduce = useReducedMotion() ?? false;
  const baseId = useId();
  const text = { ...DEFAULT_LABELS, ...labels };
  const [copied, setCopied] = useState(false);
  const [internalFeedback, setInternalFeedback] =
    useState<StreamingResponseFeedback>(defaultFeedback);
  const [internalSourcesOpen, setInternalSourcesOpen] =
    useState(defaultSourcesOpen);
  const copyTimer = useRef<number | undefined>(undefined);
  const currentFeedback = feedback ?? internalFeedback;
  const currentSourcesOpen = sourcesOpen ?? internalSourcesOpen;
  const streaming = status === "streaming";
  const complete = status === "complete";
  const canCopy = Boolean(copyText || onCopy);
  const hasSources = sources.length > 0;
  const shouldShowActions =
    showActions &&
    !streaming &&
    (canCopy || onRetry || complete || hasSources || Boolean(extraActions));
  const sourcesContentId = `${baseId}-sources`;
  const resolvedSourcePrefix =
    sourceIdPrefix ?? `response-source-${baseId.replace(/:/g, "")}`;

  useEffect(
    () => () => {
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
    },
    [],
  );

  const handleCopy = useCallback(async () => {
    if (onCopy) await onCopy();
    else if (copyText) await navigator.clipboard?.writeText(copyText);

    setCopied(true);
    if (copyTimer.current) window.clearTimeout(copyTimer.current);
    copyTimer.current = window.setTimeout(() => setCopied(false), 1600);
  }, [copyText, onCopy]);

  const setFeedback = (next: Exclude<StreamingResponseFeedback, null>) => {
    const value = currentFeedback === next ? null : next;
    if (feedback === undefined) setInternalFeedback(value);
    onFeedbackChange?.(value);
  };

  const setSourcesOpen = useCallback(
    (next: boolean) => {
      if (sourcesOpen === undefined) setInternalSourcesOpen(next);
      onSourcesOpenChange?.(next);
    },
    [onSourcesOpenChange, sourcesOpen],
  );

  return (
    <div
      data-state={status}
      aria-busy={streaming}
      className={cn("w-full", className)}
    >
      <div
        aria-live={announce ? "polite" : "off"}
        className={cn(
          "text-sm leading-6 text-foreground/90 [&_a]:font-medium [&_a]:underline [&_a]:underline-offset-4 [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.9em] [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:space-y-1 [&_ol]:pl-5 [&_p+p]:mt-3 [&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-xl [&_pre]:border [&_pre]:border-border [&_pre]:bg-muted/45 [&_pre]:p-3 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_ul]:my-3 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5",
          contentClassName,
        )}
      >
        {children}
      </div>

      <AnimatePresence initial={false}>
        {shouldShowActions ? (
          <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0.12 : 0.22, ease: EASE_OUT }}
            className="mt-3"
          >
            <div
              data-testid={testIds?.actions}
              className={cn("flex items-center gap-0.5", actionsClassName)}
            >
              {canCopy ? (
                <ResponseAction
                  label={copied ? text.copied : text.copy}
                  testId={testIds?.copy}
                  disabled={actionsDisabled}
                  onClick={handleCopy}
                >
                  {copied ? (
                    <Check className="size-3.5" />
                  ) : (
                    <Copy className="size-3.5" />
                  )}
                </ResponseAction>
              ) : null}
              {onRetry ? (
                <ResponseAction
                  label={text.retry}
                  testId={testIds?.retry}
                  disabled={actionsDisabled}
                  onClick={onRetry}
                >
                  <RotateCcw className="size-3.5" />
                </ResponseAction>
              ) : null}
              {complete && showFeedback ? (
                <>
                  <ResponseAction
                    label={text.helpful}
                    testId={testIds?.helpful}
                    active={currentFeedback === "up"}
                    pressed={currentFeedback === "up"}
                    disabled={actionsDisabled}
                    onClick={() => setFeedback("up")}
                  >
                    <ThumbsUp className="size-3.5" />
                  </ResponseAction>
                  <ResponseAction
                    label={text.notHelpful}
                    testId={testIds?.notHelpful}
                    active={currentFeedback === "down"}
                    pressed={currentFeedback === "down"}
                    disabled={actionsDisabled}
                    onClick={() => setFeedback("down")}
                  >
                    <ThumbsDown className="size-3.5" />
                  </ResponseAction>
                </>
              ) : null}
              {hasSources ? (
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                <button
                  type="button"
                  data-testid={testIds?.sources}
                  aria-expanded={currentSourcesOpen}
                  aria-controls={sourcesContentId}
                  onClick={() => setSourcesOpen(!currentSourcesOpen)}
                  className="group ml-1 inline-flex min-h-7 items-center gap-2 rounded-md px-1.5 text-xs text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <CitationStack citations={sources} />
                  <span className="tabular-nums">
                    {sources.length}{" "}
                    {sources.length === 1 ? text.source : text.sources}
                  </span>
                  <motion.span
                    aria-hidden="true"
                    animate={{ rotate: currentSourcesOpen ? 180 : 0 }}
                    transition={reduce ? { duration: 0 } : SPRING_SWAP}
                    className="text-muted-foreground/50 group-hover:text-muted-foreground"
                  >
                    <ChevronDown className="size-3" />
                  </motion.span>
                </button>
                    </TooltipTrigger>
                    <TooltipContent className="text-sm font-medium">
                      {currentSourcesOpen ? text.hideSources : text.showSources}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : null}
              {extraActions}
            </div>

            {hasSources ? (
              <BouncyDisclosurePanel
                id={sourcesContentId}
                open={currentSourcesOpen}
                reduce={reduce}
              >
                <CitationList
                  citations={sources}
                  idPrefix={resolvedSourcePrefix}
                  className={cn("mt-2 rounded-xl bg-muted p-2", sourcesClassName)}
                />
              </BouncyDisclosurePanel>
            ) : null}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
